import { Link } from "react-router-dom";
import { absUrl } from "./media";
import { renderInline } from "./StoryChapterBlock";

const Wrap = ({ to, className, children, testId, id, nested }) => {
  if (!to) return <div id={id} className={className} data-testid={testId}>{children}</div>;
  const ext = /^https?:/i.test(to);
  if (nested) {
    // Inside another link: no <a> in <a>. Act like a link without being one.
    const go = (e) => { e.preventDefault(); e.stopPropagation(); if (ext) window.open(to, "_blank", "noopener,noreferrer"); else window.location.assign(to); };
    return <span role="link" tabIndex={0} onClick={go} onKeyDown={(e) => e.key === "Enter" && go(e)} className={`${className} cursor-pointer`} data-testid={testId}>{children}</span>;
  }
  return ext
    ? <a id={id} href={to} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>
    : <Link id={id} to={to} className={className} data-testid={testId}>{children}</Link>;
};

// Featured badge — the compact dark "event card" used on the home page:
// round/square event image + eyebrow + title + bold headline, then a
// sponsor/partner strip (logo + line) underneath. Fully admin-editable.
// "HOliday HOpe HOp" → HO HO HO bold, the rest same-size caps.
const smallCaps = (text) => String(text || "").split(/([a-z]+)/).filter(Boolean).map((part, i) =>
  /^[a-z]+$/.test(part)
    ? <span key={i} className="font-medium">{part.toUpperCase()}</span>
    : <span key={i} className="font-black">{part}</span>);

const FeaturedBadgeBlock = ({ block }) => {
  const cfg = block.config || {};
  const now = Date.now();
  if (cfg.show_from && now < new Date(cfg.show_from).getTime()) return null;
  if (cfg.show_until && now > new Date(cfg.show_until).getTime()) return null;
  const bgImage = cfg.background_image ? absUrl(cfg.background_image) : null;
  const light = cfg.theme === "light" && !bgImage;
  const accent = cfg.accent || (light ? "#1A1A1A" : "#F2CC8F");
  const round = cfg.image_shape !== "square" && cfg.image_shape !== "wide";
  const wide = cfg.image_shape === "wide";
  const tx = light ? "text-[#1A1A1A]" : "text-white";
  const sub = light ? "text-[#3a3a3a]" : "text-white/85";
  const strip = light ? "bg-black/5 hover:bg-black/10" : "bg-white/5 hover:bg-white/10";
  const title = cfg.title_smallcaps ? smallCaps(block.title) : block.title;
  const cardStyle = bgImage
    ? { borderColor: accent, backgroundImage: `linear-gradient(90deg, rgba(6,14,32,0.82) 0%, rgba(6,14,32,0.55) 45%, rgba(6,14,32,0.25) 100%), url(${bgImage})`, backgroundSize: "cover", backgroundPosition: cfg.background_focus || "center" }
    : { borderColor: accent };
  return (
    <Wrap to={cfg.link} id={cfg.anchor || undefined} className="block no-underline scroll-mt-28" testId="featured-badge">
      <div className={`${light ? "bg-white" : "bg-[#0A1626]"} rounded-2xl p-4 sm:p-5 border-2 hover:shadow-xl transition-shadow overflow-hidden ${bgImage ? "[text-shadow:0_1px_6px_rgba(0,0,0,0.6)]" : ""}`} style={cardStyle} data-testid={bgImage ? "featured-badge-bg" : undefined}>
        <div className="flex items-center gap-3 sm:gap-5">
          {cfg.image && (
            <img src={absUrl(cfg.image)} alt={cfg.image_alt || block.title || ""} loading="lazy"
              className={`${wide ? "h-20 w-32 sm:h-32 sm:w-52" : "h-20 w-20 sm:h-32 sm:w-32"} shadow-md shrink-0 object-cover bg-white ${round ? "rounded-full" : "rounded-xl"}`} style={cfg.image_focus ? { objectPosition: cfg.image_focus } : undefined} />
          )}
          <div className="flex-1 min-w-0">
            {cfg.eyebrow && <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] mb-1 leading-snug" style={{ color: accent }}>{cfg.eyebrow}</p>}
            {block.title && <p className={`${tx} font-['Playfair_Display'] text-xl sm:text-3xl font-semibold leading-tight [text-wrap:balance]`}>{title}</p>}
            {cfg.headline && <p className={`hidden sm:block ${tx} text-lg font-semibold leading-snug mt-1.5`}>{renderInline(cfg.headline, accent)}</p>}
          </div>
        </div>
        {cfg.headline && <p className={`sm:hidden ${tx} text-sm font-semibold leading-snug mt-3`}>{renderInline(cfg.headline, accent)}</p>}
        {cfg.button_label && (
          <div className={`mt-3 flex ${wide ? "" : "sm:ml-[9.25rem]"}`}>
            <span className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-extrabold shadow-md transition-transform hover:scale-[1.03]" style={{ backgroundColor: accent, color: light ? "#FFFFFF" : "#0A1626" }} data-testid="featured-badge-button">
              {cfg.button_label} →
            </span>
          </div>
        )}
        {(cfg.sponsor_image || cfg.sponsor_text) && (
          <Wrap to={cfg.sponsor_link} nested={!!cfg.link} className={`mt-3 flex items-center gap-3 rounded-xl ${strip} transition-colors p-2 sm:p-2.5 ${wide ? "" : "sm:ml-[9.25rem]"} no-underline`} testId="featured-badge-sponsor">
            {cfg.sponsor_image && (cfg.sponsor_image_style === "photo"
              ? <img src={absUrl(cfg.sponsor_image)} alt={cfg.sponsor_alt || ""} loading="lazy" className="h-16 w-28 sm:h-24 sm:w-44 rounded-lg shrink-0 object-cover shadow-md" />
              : <img src={absUrl(cfg.sponsor_image)} alt={cfg.sponsor_alt || ""} loading="lazy" className="h-11 sm:h-14 w-auto max-w-[6rem] rounded-md bg-white p-1 shrink-0 object-contain" />)}
            {cfg.sponsor_text && <p className={`${sub} text-xs sm:text-base leading-snug min-w-0 ${light ? "[&_strong]:text-[#1A1A1A]" : "[&_strong]:text-white"}`}>{renderInline(cfg.sponsor_text, light ? "#1A1A1A" : "#FFFFFF")}</p>}
          </Wrap>
        )}
      </div>
    </Wrap>
  );
};

export default FeaturedBadgeBlock;
